import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { PaginatedColocApiConnectedDropDown } from '@/features/shared/feature-paginated-coloc-api-connected-dropdown';

import { usePairTransactionWithRentContext } from '@/features/admin/transactions/components/incoming-transactions-list/hooks/use-pair-transaction-with-rent';
import { useGetRoomsWithTenants, RoomWithTenant } from '@/features/admin/transactions/components/incoming-transactions-list/hooks/use-get-rooms-with-tenants';
import { House } from '@/types/admin';

const PairTransactionRoomTenantSelectionDropDown = () => {
  const {
    selectedHouse,
    selectedRoom,
    selectedTenant,
    setSelectedHouse,
    setSelectedRoom,
    setSelectedTenant,
    roomsQuery,
    setRoomsQuery,
  } = usePairTransactionWithRentContext()!;

  const { t } = useTranslation();

  const handleChange = useCallback(
    (value: RoomWithTenant | undefined) => {
      if (value) {
        // Set the house from the room's colocation
        if (value.room.house) {
          // Type assertion: house dropdown only needs id and name for display
          setSelectedHouse(value.room.house as House);
        }
        setSelectedRoom(value.room);
        setSelectedTenant(value.tenant);
      } else {
        setSelectedRoom(undefined);
        setSelectedTenant(undefined);
      }
    },
    [setSelectedHouse, setSelectedRoom, setSelectedTenant]
  );

  const dataSource = useGetRoomsWithTenants({
    searchQuery: roomsQuery,
    ...(selectedHouse && { selectedHouseId: selectedHouse.id }),
  })!;

  const selectedValue = React.useMemo(() => {
    if (selectedRoom && selectedTenant && dataSource.fetch.data?.pages) {
      // Find the matching RoomWithTenant from all pages
      for (const page of dataSource.fetch.data.pages) {
        const found = page.data.data.find(
          (rt) => rt.room.id === selectedRoom.id && rt.tenant.id === selectedTenant.id
        );
        if (found) return found;
      }
    }
    return undefined;
  }, [selectedRoom, selectedTenant, dataSource.fetch.data?.pages]);

  const displayValue = React.useMemo(() => {
    if (selectedRoom && selectedTenant) {
      return `#${selectedRoom.room_number} - ${selectedTenant.first_name} ${selectedTenant.last_name}`;
    }
    return '';
  }, [selectedRoom, selectedTenant]);

  return (
    <PaginatedColocApiConnectedDropDown<RoomWithTenant>
      disabled={false}
      placeholder={
        t(
          'js.features.admin.transactions.transaction_pairing_modal.room_tenant_selection_placeholder'
        ) ?? 'Select a room and tenant'
      }
      className="z-40 w-full"
      showLabel
      label={t(
        'js.features.admin.transactions.transaction_pairing_modal.room_tenant_selection_label'
      )}
      value={selectedValue}
      onChange={handleChange}
      {...(displayValue && { displayValue })}
      onTextInputChange={(event) => setRoomsQuery(event.target.value)}
      dataSource={dataSource}
      itemRenderer={(itemProps, ref) => {
        return (
          // @ts-ignore
          <DropDown.DropDownOptionDefaultContent ref={ref}>
            {`#${itemProps.room.room_number} - ${itemProps.tenant.first_name} ${itemProps.tenant.last_name}${itemProps.room.house ? ` (${itemProps.room.house.name})` : ''}`}
          </DropDown.DropDownOptionDefaultContent>
        );
      }}
    />
  );
};

export { PairTransactionRoomTenantSelectionDropDown };

